import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRef } from "react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { useSlug, slugify } from "@/hooks/useSlug";
import { dateTimeLocalToTs } from "@/lib/datetime";

const schema = z.object({
  title: z.string().min(2).max(200),
  slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/),
  starts_at_iso: z.string().min(1, "Required"),
  fee_inr_rupees: z.coerce.number().min(0).max(1_000_000),
});
type FormValues = z.infer<typeof schema>;

export const Route = createFileRoute("/_app/events/new")({ component: NewEvent });

function NewEvent() {
  const navigate = useNavigate();
  const slugTouched = useRef(false);
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { title: "", slug: "", starts_at_iso: "", fee_inr_rupees: 0 },
  });
  useSlug({
    title: watch("title"), currentSlug: watch("slug"),
    setSlug: (s) => setValue("slug", s, { shouldValidate: true }),
    slugTouched,
  });

  const create = useMutation({
    mutationFn: (v: FormValues) => api.post<{ id: number }>("/admin/events", {
      title: v.title,
      slug: v.slug,
      starts_at: dateTimeLocalToTs(v.starts_at_iso),
      fee_inr: Math.round(v.fee_inr_rupees * 100), // store paise
    }),
    onSuccess: (r) => {
      toast.success("Event created");
      navigate({ to: "/events/$id" as never, params: { id: String(r.id) } });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Create failed"),
  });

  return (
    <div className="max-w-2xl">
      <PageHeader title="New event" back={{ href: "/events", label: "Events" }} />
      <Card>
        <CardContent className="pt-6">
          <form className="space-y-4" onSubmit={handleSubmit((v) => create.mutate(v))}>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input id="title" placeholder="ENTHUSIA '26" {...register("title")} />
              {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">URL slug</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">/events/</span>
                <Input id="slug" {...register("slug", { onChange: () => { slugTouched.current = true; } })}
                  onBlur={(e) => setValue("slug", slugify(e.target.value), { shouldValidate: true })} />
              </div>
              {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="starts_at_iso">Starts at</Label>
                <Input id="starts_at_iso" type="datetime-local" {...register("starts_at_iso")} />
                {errors.starts_at_iso && <p className="text-xs text-destructive">{errors.starts_at_iso.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="fee_inr_rupees">Registration fee (₹)</Label>
                <Input id="fee_inr_rupees" type="number" min={0} step="0.01" {...register("fee_inr_rupees")} />
                <p className="text-xs text-muted-foreground">0 = free event (no payment required).</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => navigate({ to: "/events" as never })}>Cancel</Button>
              <Button type="submit" disabled={create.isPending}>{create.isPending ? "Creating…" : "Create event"}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
